import { TextFieldModule } from '@angular/cdk/text-field';
import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
  EntityStateDto,
  EntityStatus,
  IdentityVerificationStatus,
  PatchEntityStateDto,
  UserForAdminDto,
  UserRoleRepresentationDto,
  UserRolesRepresentationDto,
} from '@workspace/data-types';
import {
  AlertSnackBarService,
  AuraClipboardComponent,
  AvatarAuraComponent,
  CodeBlockComponent,
  EntityStateDialogAuraComponent,
  IdentityStatusComponent,
  LuxonDatePipe,
  StateStatusChipComponent,
} from '@workspace/ng-aura';
import { environment } from '@workspace/ng-config';
import { GenderDisplayPipe, SessionStorageKey } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { DateTime } from 'luxon';
import { CookieService } from 'ngx-cookie-service';
import { filter, switchMap, tap } from 'rxjs';
import { IdentityReviewBannerCoreComponent } from '../identity-review-banner/identity-review-banner.component';
import { UserRolesEditAdminCoreComponent } from '../roles/edit/roles-edit.component';

@Component({
  selector: 'core-user-details',
  templateUrl: './user-details.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MatAccordion,
    MatExpansionModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatRippleModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatDatepickerModule,
    TextFieldModule,
    LuxonDatePipe,
    GenderDisplayPipe,
    TitleCasePipe,
    AuraClipboardComponent,
    AvatarAuraComponent,
    CodeBlockComponent,
    IdentityStatusComponent,
    MatDivider,
    StateStatusChipComponent,
    MatMenuModule,
    MatMenuTrigger,
    UserRolesEditAdminCoreComponent,
    IdentityReviewBannerCoreComponent,
  ],
})
export class UserDetailsAdminCoreComponent {
  private readonly COOKIE_DOMAIN = `.${environment.domain}`;

  private api = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private alertSnackbar = inject(AlertSnackBarService);
  private cookieService = inject(CookieService);

  userStateUpdated = output<EntityStateDto>();
  userUpdated = output<UserForAdminDto>();
  entityStatus = EntityStatus;
  identityVerificationStatus = IdentityVerificationStatus;
  user = input.required<UserForAdminDto>();
  editMode = signal<boolean>(false);
  closeRolesEditing = signal<boolean>(false);

  readonly age = computed(() => {
    const birthDate = this.user().identity.birthDate;
    if (!birthDate) return null;

    const birth = DateTime.fromISO(birthDate);
    const now = DateTime.now();
    return Math.trunc(now.diff(birth, 'years').years); // Integer age
  });

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode.set(!this.editMode);
    } else {
      this.editMode.set(editMode);
    }
  }

  updateVerificationStatus(status: IdentityVerificationStatus): void {
    this.api
      .admin()
      .user(this.user()._id)
      .patch()
      .verificationStatus(status)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedUser: UserForAdminDto) => {
          this.alertSnackbar.success({ message: 'Statut de vérification mis à jour avec succès' });
          this.userUpdated.emit(updatedUser);
        }),
      )
      .subscribe();
  }

  impersonateUser() {
    this.api
      .admin()
      .user(this.user()._id)
      .impersonate()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.cookieService.set(
            SessionStorageKey.IMPERSONATE_USER_ID,
            this.user()._id.toString(),
            1,
            '/',
            this.COOKIE_DOMAIN,
            true,
            'Lax',
          );
          window.location.reload();
        }),
      )
      .subscribe();
  }

  activateUser() {
    this.api
      .admin()
      .user(this.user()._id)
      .patch()
      .state({
        status: EntityStatus.ACTIVE,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((patchedEntityState: EntityStateDto) => this.userStateUpdated.emit(patchedEntityState)),
      )
      .subscribe();
  }

  openPatchStateDialog(status: EntityStatus): void {
    this.dialog
      .open(EntityStateDialogAuraComponent, {
        data: { status, label: 'Ce compte', deletionConfirmation: true },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((success) => !!success),
        switchMap((patchEntityState: PatchEntityStateDto) =>
          this.api.admin().user(this.user()._id).patch().state(patchEntityState),
        ),
        tap((patchedEntityState: EntityStateDto) => this.userStateUpdated.emit(patchedEntityState)),
      )
      .subscribe();
  }

  saveRoles(userRoles: UserRolesRepresentationDto) {
    this.api
      .admin()
      .user(this.user()._id)
      .patch()
      .roles(userRoles)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userRoles: UserRoleRepresentationDto[]) => {
          if (userRoles) {
            this.alertSnackbar.success({
              title: 'Modification des roles',
              message: "Les roles de l'utilisateur ont été modifié avec succès",
            });
            this.user().roles = userRoles;
            this.closeRolesEditing.set(true);
          }
        }),
      )
      .subscribe();
    this.closeRolesEditing.set(false);
  }
}
